import type { CountryCodeTBB, LangTBB } from '@src/i18n/locales'
import { createPrefixedCookies } from '@src/utils/cookies.util'

export const COOKIES = {
  ...createPrefixedCookies({
    session: 'bodi-session',
    akamaiSession: 'bodi-akamai-session',
    /** The code verifier to use in the code exchange for the given code challenge ('PKCE') */
    akamaiCodeVerifier: 'bodi-akamai-code-verifier',
    /** Ensures the same callback URL is used for token request and code exchange */
    akamaiCallbackUrl: 'bodi-akamai-callback-url',
    /** Where to redirect user after auth flow is complete. */
    akamaiRedirectUrl: 'bodi-akamai-redirect-url',
    /** Skip SSO check on auth callback requests. */
    akamaiComingFromCallback: 'bodi-akamai-coming-from-callback',
    /** Ensures the SSO check is done once until a page is finally loaded. */
    akamaiCheckingSSO: 'bodi-akamai-checking-sso',
    /** Tracks when SSO was last checked to allow same-site SSO checks periodically. */
    akamaiSSOCheckedRecently: 'bodi-akamai-sso-checked-recently',

    auth0Session: 'bodi-auth0-session',
    /** Ensures the same callback URL is used for token request and code exchange */
    auth0CallbackUrl: 'bodi-auth0-callback-url',
    /** Where to redirect user after Auth0 auth flow is complete. */
    auth0RedirectUrl: 'bodi-auth0-redirect-url',
    /** Skip SSO check on Auth0 callback requests. */
    auth0ComingFromCallback: 'bodi-auth0-coming-from-callback',
    /** Ensures the Auth0 SSO check is done once until a page is finally loaded. */
    auth0CheckingSSO: 'bodi-auth0-checking-sso',
    /** Tracks when SSO was last checked to allow same-site SSO checks periodically. */
    auth0SSOCheckedRecently: 'bodi-auth0-sso-checked-recently',

    order: 'bodi-order',
    passwordCreated: 'bodi-password-created',
    royaltyCode: 'bodi-royalty-code',
    webSourceCode: 'bodi-web-source-code',
    upsellFromSkus: 'bodi-upsell-from-skus',
    upsellToSkus: 'bodi-upsell-to-skus',
    recaptcha: 'bodi-recaptcha',
    recaptchaToken: 'bodi-recaptcha-token',
    /** Tracks the redirects applied by the Flow Router (to expose Amplitude experiments and clear App Router cache). */
    flowRedirectApplied: 'bodi-flow-redirect-applied',
    userProperties: 'bodi-user-properties',
    inspectEnabled: 'bodi-inspect-enabled',
    affiliateClickId: 'bodi-affiliate-click-id',
    socialladderAmbassadorId: 'bodi-socialladder-ambassador-id',
    /** Tracks if the user has interacted with the geo redirection modal. Shows the modal if not. */
    geoRedirectionModal: 'bodi-geo-redirection-modal',
    lastActivityTimestamp: 'bodi-last-activity-timestamp',
    /** Used to store whether or not the user is bypassing - for internal use only */
    bypass: 'bodi-bypass',
    sierraChatSession: 'bodi-sierra-chat-session',
    sierraLastBannerShown: 'bodi-sierra-last-banner-shown',
  }),
  /** Used to track the last url visited by the user. Used by the FlowRouter to determine from which website page the user came from. */
  lastUrl: 'bodi-last-url',
  /** Used for Vercel Deployment Protection to lock down our lower environments */
  vercelJwt: '_vercel_jwt',
  vercelJwtTracking: '_vercel_jwt_tracking',
}

export const QUERY_PARAMS = {
  gtmDisabled: 'bodi-gtm-disabled',
  locale: 'locale',
  referralProgramId: 'referralProgramId',
  trainerName: 'trainerName',
  royaltyCode: 'rc',
  sku: 'sku',
  quantity: 'quantity',
  redirectUrl: 'redirectUrl',
  redirectUrlAlt: 'redirect_url',
  sessionTransferToken: 'session_transfer_token',
  authLocale: 'authLocale',
  affiliateClickId: 'irclickid',
  socialladderAmbassadorId: 'ambassadorID',
  lcAmbassadorId: 'lc_ambassador_id',
  nextDoorClickId: 'ndclid' as const,
  bulk: 'bulk',
  /** Enables/disables the bypass by setting true or false */
  bypass: 'bypass',
  royaltyCodeAlt: 'rc',
  webSourceCodeAlt: 'code',
}

export const HEADERS = {
  acceptLanguage: 'accept-language',
  requestIp: 'bodi-request-ip',
  middlewareRewrite: 'bodi-middleware-rewrite',
  middlewareRedirect: 'bodi-middleware-redirect',
  locale: 'bodi-locale',
  method: 'bodi-method',
  url: 'bodi-url',
  host: 'bodi-host',
  lastUrl: 'bodi-last-url',
  vercelIPCountry: 'x-vercel-ip-country',
  vercelDeploymentUrl: 'x-vercel-deployment-url',
  cloudfrontHost: 'x-cloudfront-host',
  userAgent: 'user-agent',
  shouldRedirectToSSO: 'bodi-should-redirect-to-sso',
}

export const CACHE = {
  page: 'page',
  notFoundSection: '404-section',
  flowRouterRules: 'flow-router-rules',
  nutritionPDP: 'nutrition-pdp',
  digitalPDP: 'digital-pdp',
  fitnessPDP: 'fitness-pdp',
}

export const STORAGE = {
  logsVisibleInConsole: 'logsVisibleInConsole',
  logLevelClient: 'logLevelClient',
  lastActivityTimestamp: 'lastActivityTimestamp',
  sseOrders: 'sseOrders',
}

export const BROADCAST_CHANNEL = 'bodi-broadcast-channel'

export const DEFAULT_LOCALE = 'en-US'

export enum BROADCAST_TOPIC {
  LOGIN = 'login',
  LOGOUT = 'logout',
}

export enum CUSTOM_EVENTS {
  stickyFooterVisible = 'stickyFooterVisible',
  stickyFooterHidden = 'stickyFooterHidden',
}

export const EXPERIMENTS = {
  galleryExperiment: 'com-3086',
}

// time and dates
export const ONE_SECOND = 1000
export const ONE_MINUTE = ONE_SECOND * 60
export const ONE_HOUR = ONE_MINUTE * 60
export const ONE_DAY = ONE_HOUR * 24
export const ONE_DAY_IN_SECONDS = ONE_DAY / 1000
export const ONE_MONTH = ONE_DAY * 30
export const ONE_MONTH_IN_SECONDS = ONE_MONTH / 1000
export const ONE_YEAR = ONE_DAY * 365
export const ONE_YEAR_IN_SECONDS = ONE_YEAR / 1000

// 1px black image in base64
export const DEFAULT_IMG_URI =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAIAAAACCAYAAABytg0kAAAAAXNSR0IArs4c6QAAAAlwSFlzAAAWJQAAFiUBSVIk8AAAABNJREFUCB1jZGBg+A/EDEwgAgQADigBA//q6GsAAAAASUVORK5CYII%3D'

// errors
export enum ErrorCodes {
  CustomerEmailRequired = 'customer-email-required',
  IdentityOutage = 'identity-outage',
  DuplicateCustomerTypesGranted = 'duplicate-customer-types-granted',
  DuplicateEntitlementsGranted = 'duplicate-entitlements-granted',
  InvalidItems = 'invalid-items',
  InvalidQuantity = 'invalid-quantity',
  LineItemsRequired = 'line-items-required',
  InvalidShippingCountry = 'invalid-shipping-country',
  InvalidBillingCountry = 'invalid-billing-country',
  IncompleteShippingAddress = 'incomplete-shipping-address',
  ShippingMethodRequired = 'shipping-method-required',
  CcSystemError = 'cc-system-error',
  SystemError = 'system-error',
  InvalidShippingMethod = 'invalid-shipping-method',
  TaxRequired = 'tax-required',
  InvalidDiscountCodes = 'invalid-discount-codes',
  CustomerTypeConflict = 'customer-type-conflict',
  EntitlementConflict = 'entitlement-conflict',
  CcInvalidPayment = 'cc-invalid-payment',
  CcPaymentDeclined = 'cc-payment-declined',
  CcPayment3dsDeclined = 'cc-payment-3ds-declined',
  CcInvalid3dsPayment = 'cc-invalid-3ds-payment',
  CcRequired3dsPayment = 'cc-required-3ds-payment',
  ApplePayInit = 'apple-pay-init',
  CartMissing = '[client]-cart-missing',
  LineItemsWithErrors = '[client]-line-items-with-errors',
  AkamaiSSOFailure = 'akamai-sso-failure',
  AkamaiCallbackError = 'akamai-callback-validation-error',
  AkamaiSessionError = 'akamai-session-validation-error',
  Auth0SSOFailure = 'auth0-sso-failure',
  Auth0CallbackError = 'auth0-callback-validation-error',
  Auth0SessionError = 'auth0-session-validation-error',
  OAuthLogin = 'oauth-login-error',
  OAuthInvalidCredentials = 'oauth-invalid-credentials-error',
  Cybersource3DSSetupFailed = 'cybersource-3ds-setup-failed',
  Cybersource3DSCheckEnrollmentFailed = 'cybersource-3ds-check-enrollment-failed',
  Cybersource3DSPayerValidationFailed = 'cybersource-3ds-payer-validation-failed',
  Unknown = 'unknown',
}

export enum ErrorType {
  Unrecoverable = 'Unrecoverable',
  Retry = 'Retry',
  Recalculate = 'Recalculate',
  InvalidItems = 'InvalidItems',
  Eligibility = 'Eligibility',
  Payment = 'Payment',
  Unknown = 'Unknown',
  Identity = 'Identity',
  ApplePay = 'ApplePay',
}

/**
 * Error codes grouped by error type.
 * Optionally used to treat multiple errors in a single way based on the error type.
 * @example: See src/app/[country]/[lang]/checkout/modules/ErrorModal.tsx
 */
export type ErrorsGroup = Partial<Record<ErrorType, ErrorCodes[]>>

export const BuilderActionErrorsGroup: ErrorsGroup = {
  [ErrorType.Eligibility]: [ErrorCodes.CustomerTypeConflict, ErrorCodes.EntitlementConflict],
  [ErrorType.InvalidItems]: [ErrorCodes.InvalidItems],
  [ErrorType.Unknown]: [ErrorCodes.Unknown],
}

export const SignUpErrorsGroup: ErrorsGroup = {
  [ErrorType.Identity]: [ErrorCodes.IdentityOutage],
}

export const CheckoutErrorsGroup: ErrorsGroup = {
  [ErrorType.Unrecoverable]: [
    ErrorCodes.CustomerEmailRequired,
    ErrorCodes.DuplicateCustomerTypesGranted,
    ErrorCodes.DuplicateEntitlementsGranted,
  ],
  [ErrorType.Retry]: [
    ErrorCodes.LineItemsRequired,
    ErrorCodes.InvalidShippingCountry,
    ErrorCodes.InvalidBillingCountry,
    ErrorCodes.IncompleteShippingAddress,
    ErrorCodes.ShippingMethodRequired,
    ErrorCodes.CcSystemError,
    ErrorCodes.SystemError,
    ErrorCodes.Cybersource3DSSetupFailed,
    ErrorCodes.Cybersource3DSCheckEnrollmentFailed,
    ErrorCodes.Cybersource3DSPayerValidationFailed,
  ],
  [ErrorType.Recalculate]: [
    ErrorCodes.InvalidShippingMethod,
    ErrorCodes.TaxRequired,
    ErrorCodes.InvalidDiscountCodes,
  ],
  [ErrorType.InvalidItems]: [ErrorCodes.InvalidItems, ErrorCodes.LineItemsWithErrors],
  [ErrorType.Eligibility]: [ErrorCodes.CustomerTypeConflict, ErrorCodes.EntitlementConflict],
  [ErrorType.Payment]: [
    ErrorCodes.CcInvalidPayment,
    ErrorCodes.CcPaymentDeclined,
    ErrorCodes.CcPayment3dsDeclined,
    ErrorCodes.CcInvalid3dsPayment,
    ErrorCodes.CcRequired3dsPayment,
  ],
  [ErrorType.ApplePay]: [ErrorCodes.ApplePayInit],
  [ErrorType.Unknown]: [ErrorCodes.Unknown],
}

export const OrderConfirmationErrorsGroup: ErrorsGroup = {
  [ErrorType.Unknown]: [ErrorCodes.Unknown],
}

export enum BuilderPageType {
  Landing = 'Landing',
  Offer = 'Offer',
  Upsell = 'Upsell',
  CreateAccount = 'Create Account',
  Category = 'Category',
  Cart = 'Cart',
  Other = 'Other',
}

export const CONSTANTS = {
  ONE_SECOND,
  ONE_MINUTE,
  ONE_HOUR,
  ONE_DAY,
  ONE_YEAR,
}

export enum CardType {
  Visa = 'Visa',
  Mastercard = 'Mastercard',
  AMEX = 'AMEX',
  Discover = 'DISCOVER',
}

export enum CardNetwork {
  VISA = 'VISA',
  MASTERCARD = 'MASTERCARD',
  AMEX = 'AMEX',
  DISCOVER = 'DISCOVER',
  CUP = 'CUP',
}

export const CardIcons: Record<CardType, string> = {
  [CardType.Visa]: '/images/visa.svg',
  [CardType.Mastercard]: '/images/mastercard.svg',
  [CardType.AMEX]: '/images/amex.svg',
  [CardType.Discover]: '/images/discover.svg',
}

export const CyberSourceCardTypeMapping: Record<string, CardType> = {
  '001': CardType.Visa,
  '002': CardType.Mastercard,
  '003': CardType.AMEX,
  '004': CardType.Discover,
  '062': CardType.Discover,
}

export enum PayerAuthActions {
  CONSUMER_AUTHENTICATION = 'CONSUMER_AUTHENTICATION',
  VALIDATE_CONSUMER_AUTHENTICATION = 'VALIDATE_CONSUMER_AUTHENTICATION',
  TOKEN_CREATE = 'TOKEN_CREATE',
}

export const PaymentConstants = {
  ONE_DOLLAR_CENT_AMOUNT: 101,
  EMPTY_VALUE: '--',
  INSTRUMENT_IDENTIFIER: 'instrumentIdentifier',
  COMMERCE_INDICATOR: 'internet',
}

export const MIDDLEWARE_TIMEOUTS = {
  sessionMiddleware: 5000,
  flowRouterMiddleware: 5000,
  akamaiMiddleware: 5000,
  auth0Middleware: 5000,
}

export const TBB_LANGUAGE_OPTIONS = [
  { countryCode: 'US', languages: ['en', 'es'] },
  { countryCode: 'CA', languages: ['en', 'fr'] },
  { countryCode: 'GB', languages: ['en'] },
  { countryCode: 'FR', languages: ['fr'] },
] satisfies Array<{ countryCode: CountryCodeTBB; languages: LangTBB[] }>

// Builder.io page fields used to fetch basic page data (used in sitemap and SEO)
export const BUILDER_PAGE_META_FIELDS = [
  'data.url',
  'data.title',
  'data.description',
  'data.openGraphImage',
  'data.robots',
  'data.canonicalUrl',
  'data.productKey',
  'data.seoStructuredData.includedInSitemap',
  'published',
  'query',
].join(',')

export enum AuthProviders {
  AKAMAI = 'akamai',
  AUTH0 = 'auth0',
}

export const AuthRoutes = [
  '/sso',
  '/login',
  '/login-callback',
  '/login-bodweb',
  '/logout',
  '/logout-callback',
  '/session',
  '/refresh',
]

export const CocoaContentType = {
  PROGRAMS: 'programs',
  BLOCKS: 'blocks',
  COMPILATIONS: 'content',
}

export const CocoaEntityType = {
  workout: 'workout',
  lesson: 'lesson',
  promo: 'promo',
  program: 'program',
  compilation: 'compilation',
  recipe: 'recipe',
  resource: 'resource',
  editorial: 'editorial',
  bodiBlock: 'bodiBlock',
  episode: 'episode',
  relatedContent: 'relatedContent',
}

export const CocoaCollectionType = {
  default: 'default',
  compilation: 'compilation',
  program: 'program',
  bodiBlock: 'bodiBlock',
  editorial: 'editorial',
  resource: 'resource',
  showcase: 'showcase',
  relatedContent: 'relatedContent',
  mixed: 'mixed',
  meta: 'meta',
}

export const FEATURED_PROGRAMS_SLUG = 'rail-1-featured-programs'
export const RECOMMENDED_WORKOUTS_SLUG = 'custom-recommended-for-you'
